/**
 * Centralized rate limit error detection utilities
 */

import { isRateLimitError, RateLimitError } from './types';

/**
 * Checks if an error message string indicates a rate limit error
 * Used for stream errors and other string-based error detection
 */
export function isRateLimitMessage(errorMessage: string): boolean {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return false;
  }

  const lowerMessage = errorMessage.toLowerCase();

  const rateLimitIndicators = [
    'rate limit',
    'ratelimit',
    'monthly limit',
    'limit has been exceeded',
    'error code: 429',
    'error_code: 429',
    'exceeded the token rate limit',
    'rate limit reached',
    'ratelimitreached',
    'too many requests',
    'requests to gpt',
    'retry after',
    'plugininvokeerror',
    'failed to transform agent message',
    'resource_exhausted',
  ];

  return rateLimitIndicators.some(indicator => lowerMessage.includes(indicator));
}

/**
 * Checks if an error or response indicates a rate limit
 * This is the main detection function that should be used throughout the app
 */
export function detectRateLimit(
  error: unknown,
  response?: Response
): { isRateLimit: boolean; isMonthlyLimit: boolean } {
  // Check response status first (most reliable)
  if (response?.status === 429) {
    return { isRateLimit: true, isMonthlyLimit: true };
  }
  
  // Check if it's a RateLimitError object
  if (isRateLimitError(error)) {
    const err = error as RateLimitError;
    return {
      isRateLimit: true,
      isMonthlyLimit: err.isMonthlyLimit ?? err.status === 429,
    };
  }
  
  // Check error message for rate limit indicators
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    const message = typeof err.message === 'string' ? err.message : '';
    
    if (message && isRateLimitMessage(message)) {
      return { isRateLimit: true, isMonthlyLimit: message.includes('monthly limit') };
    }
    
    // Check status property
    if (err.status === 429) {
      return { isRateLimit: true, isMonthlyLimit: true };
    }
  }
  
  return { isRateLimit: false, isMonthlyLimit: false };
}

